import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "@/api/client";
import { parseApiResponse } from "@/api/responseSchemas";

const mcpTokenMetadataSchema = z.object({
  id: z.number(),
  label: z.string(),
  created_at: z.string(),
  last_used_at: z.string().nullable(),
  expires_at: z.string(),
  revoked_at: z.string().nullable(),
});

const createMcpTokenResponseSchema = mcpTokenMetadataSchema.extend({ token: z.string().min(1) });

export type McpTokenMetadata = z.infer<typeof mcpTokenMetadataSchema>;
export type CreateMcpTokenResponse = z.infer<typeof createMcpTokenResponseSchema>;

export const mcpTokensQueryKey = (userId: string | undefined) => ["mcpTokens", userId] as const;

export const useMcpTokens = (userId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: mcpTokensQueryKey(userId),
    queryFn: getMcpTokens,
    enabled: enabled && userId !== undefined,
  });

export const getMcpTokens = async () => {
  const response = await apiClient.get("/mcp-tokens");
  return parseApiResponse(
    z.array(mcpTokenMetadataSchema),
    response.data,
    "GET /mcp-tokens",
  );
};

export const createMcpToken = async (label: string, expiresInDays: number) => {
  const response = await apiClient.post("/mcp-tokens", {
    label,
    expires_in_days: expiresInDays,
  });
  return parseApiResponse(
    createMcpTokenResponseSchema,
    response.data,
    "POST /mcp-tokens",
  );
};

export const revokeMcpToken = (tokenId: number) =>
  apiClient.delete(`/mcp-tokens/${tokenId}`);
